import { useState, type CSSProperties, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, Input, SegmentedControl } from "@/design-system";
import {
  useGrantMembership,
  useRechargeCredits,
  useReverseCredit,
  useStudentCredits,
  useStudentMemberships,
} from "@/api/queries";
import { formatLongDate, formatShortDate } from "@/lib/format";
import { displayName } from "@/lib/user";
import {
  activeMembership,
  addDays,
  creditKindLabelKey,
  formatCreditAmount,
  isReversible,
  latestMembership,
  remainingReversible,
  todayIso,
} from "@/lib/billing";
import { useOnline } from "@/lib/useOnline";
import { OfflineErrorState } from "@/components/OfflineErrorState";
import { SHEET_OVERLAY_STYLE, SHEET_STYLE } from "@/components/sheetStyles";
import type { CreditEntry, Student } from "@/lib/types";

/**
 * Teacher billing panel on the student detail sheet (issue #248).
 *
 * Owns the whole entitlement surface for one student:
 *
 *   - membership status + **Rinnova** (1 month) and a custom end date;
 *   - credit balance + **Ricarica** (presets + free amount + note);
 *   - the full ledger history (kind, amount, note, author, date).
 *
 * The roster already carries `credit_balance` / `membership`, so the panel
 * renders instantly and then refreshes from the per-student queries, which
 * the recharge / membership mutations invalidate.
 */

const SECTION_STYLE: CSSProperties = {
  marginTop: "var(--space-5)",
  borderTop: "1px solid var(--border-subtle)",
  paddingTop: "var(--space-4)",
};

const LABEL_STYLE: CSSProperties = {
  font: "var(--fw-semibold) var(--fs-2xs)/1 var(--font-body)",
  letterSpacing: "var(--ls-wide)",
  textTransform: "uppercase",
  color: "var(--text-faint)",
};

const ERROR_STYLE: CSSProperties = {
  background: "var(--status-danger-bg)",
  color: "var(--status-danger-fg)",
  padding: "var(--space-3) var(--space-4)",
  borderRadius: "var(--radius-md)",
  font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
};

const PRESETS = [1, 5, 10];

/** Validity presets in calendar months (issue #327). */
const VALIDITY_MONTHS = [1, 3, 4, 6, 12];

export function StudentBillingPanel({
  student,
  token,
}: {
  student: Student;
  token: string | null;
}) {
  const { t } = useTranslation();
  const online = useOnline();

  const membershipsQuery = useStudentMemberships(token, student.id);
  const creditsQuery = useStudentCredits(token, student.id);
  const grantMutation = useGrantMembership(token);
  const rechargeMutation = useRechargeCredits(token);
  const reverseMutation = useReverseCredit(token);

  const memberships = membershipsQuery.data ?? [];
  const active = activeMembership(memberships) ?? student.membership;
  const balance = creditsQuery.data?.balance ?? student.credit_balance;
  const entries: CreditEntry[] = creditsQuery.data?.entries ?? [];

  const [amount, setAmount] = useState("1");
  const [note, setNote] = useState("");
  // Validity window for the next recharge (issue #327). `validity` is a
  // month preset ("none" disables expiry); `validUntil` is an explicit date
  // that, when set, overrides the preset.
  const [validity, setValidity] = useState("none");
  const [validUntil, setValidUntil] = useState("");
  const [endDate, setEndDate] = useState("");
  // The entry currently being reversed, plus the focused modal's fields
  // (issue #311). `null` when the modal is closed.
  const [reversing, setReversing] = useState<CreditEntry | null>(null);
  const [reverseAmount, setReverseAmount] = useState("");
  const [reverseNote, setReverseNote] = useState("");

  const busy = grantMutation.isPending || rechargeMutation.isPending || reverseMutation.isPending;

  // A renewal grows the student's latest period in place, so the teacher
  // sees the current end and cannot pick a new end that is not later.
  const today = todayIso();
  const latest = latestMembership(memberships);
  const currentEnd = latest?.ends_on ?? null;
  const minEnd = currentEnd ? addDays(currentEnd, 1) : today;
  const customEndInvalid = endDate !== "" && endDate < minEnd;
  const grantFieldError = grantMutation.error?.body?.errors?.ends_on?.[0];
  const grantErrorText =
    grantFieldError ?? grantMutation.error?.message ?? t("teacher.membershipError");

  function recharge() {
    const value = Math.floor(Number(amount));
    if (!Number.isFinite(value) || value <= 0) return;

    rechargeMutation.mutate(
      {
        studentId: student.id,
        amount: value,
        note: note.trim() === "" ? undefined : note.trim(),
        // An explicit date wins over a month preset (mirrors the server).
        expiresOn: validUntil === "" ? undefined : validUntil,
        validMonths:
          validUntil === "" && validity !== "none" ? Number(validity) : undefined,
      },
      {
        onSuccess: () => {
          setNote("");
          setValidity("none");
          setValidUntil("");
        },
      },
    );
  }

  function renew() {
    grantMutation.mutate({ studentId: student.id, months: 1 });
  }

  // Open the reversal modal pre-filled with the full remaining amount; the
  // teacher can lower it for a partial correction (issue #311).
  function startReverse(entry: CreditEntry) {
    setReversing(entry);
    setReverseAmount(String(remainingReversible(entry, entries)));
    setReverseNote("");
  }

  function closeReverse() {
    setReversing(null);
    setReverseAmount("");
    setReverseNote("");
  }

  const reverseMax = reversing ? remainingReversible(reversing, entries) : 0;
  const reverseValue = Math.floor(Number(reverseAmount));
  const reverseAmountInvalid =
    !Number.isFinite(reverseValue) || reverseValue <= 0 || reverseValue > reverseMax;
  const reverseNoteMissing = reverseNote.trim() === "";
  // Map the domain 422 tags to friendly copy; fall back to the server
  // message for anything else.
  const reverseErrorText = (() => {
    switch (reverseMutation.error?.body?.error) {
      case "over_reversal":
        return t("teacher.reverseErrorOver");
      case "insufficient_balance":
        return t("teacher.reverseErrorBalance");
      case "not_reversible":
        return t("teacher.reverseErrorNotReversible");
      case "invalid_amount":
        return t("teacher.reverseErrorAmount");
      default:
        return reverseMutation.error?.message ?? t("teacher.reverseError");
    }
  })();

  function confirmReverse() {
    if (!reversing || reverseAmountInvalid || reverseNoteMissing) return;
    reverseMutation.mutate(
      {
        studentId: student.id,
        entryId: reversing.id,
        amount: reverseValue,
        note: reverseNote.trim(),
      },
      { onSuccess: () => closeReverse() },
    );
  }

  function setCustomEnd() {
    if (endDate === "" || endDate < minEnd) return;
    grantMutation.mutate(
      { studentId: student.id, endsOn: endDate },
      { onSuccess: () => setEndDate("") },
    );
  }

  return (
    <div data-testid="student-billing-panel" style={SECTION_STYLE}>
      <div style={{ ...LABEL_STYLE, marginBottom: 10 }}>{t("teacher.billingHeading")}</div>

      {/* membership */}
      <div data-testid="student-billing-membership">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            // The status pill is as wide as its label, so on a narrow sheet it
            // drops to its own line (kept hard right by the auto margin)
            // instead of pushing the heading off the edge.
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <span style={{ font: "var(--fw-semibold) var(--fs-sm)/1.2 var(--font-body)", color: "var(--text-strong)" }}>
            {t("teacher.membershipHeading")}
          </span>
          {active ? (
            <Badge
              tone="success"
              dot
              data-testid="student-billing-membership-status"
              style={{ marginLeft: "auto" }}
            >
              {/* Short date: the long form made the pill 268px wide. The full
                  date is one line below in the period hint. */}
              {t("teacher.membershipActiveUntil", { date: formatShortDate(active.ends_on) })}
            </Badge>
          ) : (
            <span
              data-testid="student-billing-membership-status"
              style={{
                marginLeft: "auto",
                font: "var(--fw-medium) var(--fs-sm)/1.2 var(--font-body)",
                color: "var(--text-muted)",
              }}
            >
              {t("teacher.membershipNone")}
            </span>
          )}
        </div>

        <Button
          variant="secondary"
          size="sm"
          disabled={busy || !online}
          onClick={renew}
          data-testid="student-billing-renew"
          style={{ marginTop: 10 }}
        >
          {t("teacher.membershipRenew")}
        </Button>

        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 8, marginTop: 10 }}>
          <div style={{ flex: "1 1 160px", minWidth: 0 }}>
            <Input
              type="date"
              label={t("teacher.membershipEndDateLabel")}
              min={minEnd}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="student-billing-end-date"
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            disabled={busy || !online || endDate === "" || customEndInvalid}
            onClick={setCustomEnd}
            data-testid="student-billing-set-end"
            style={{ marginLeft: "auto" }}
          >
            {t("teacher.membershipSetEnd")}
          </Button>
        </div>

        <div
          data-testid="student-billing-period-hint"
          style={{
            marginTop: 6,
            font: "var(--fw-regular) var(--fs-2xs)/1.4 var(--font-body)",
            color: customEndInvalid ? "var(--status-danger-fg)" : "var(--text-faint)",
          }}
        >
          {endDate === ""
            ? currentEnd
              ? t("teacher.membershipCurrentEnd", {
                  date: formatShortDate(currentEnd),
                })
              : t("teacher.membershipNoCurrent")
            : customEndInvalid
              ? t("teacher.membershipEndAfter", { date: formatShortDate(minEnd) })
              : currentEnd
                ? t("teacher.membershipExtendTo", {
                    date: formatShortDate(endDate),
                  })
                : t("teacher.membershipNewPeriod", {
                    start: formatShortDate(today),
                    end: formatShortDate(endDate),
                  })}
        </div>

        {grantMutation.isError && (
          <div role="alert" data-testid="student-billing-membership-error" style={{ ...ERROR_STYLE, marginTop: 10 }}>
            {grantErrorText}
          </div>
        )}
      </div>

      {/* credits */}
      <div style={{ ...SECTION_STYLE, marginTop: "var(--space-4)", borderTop: "none", paddingTop: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <span style={{ font: "var(--fw-semibold) var(--fs-sm)/1.2 var(--font-body)", color: "var(--text-strong)" }}>
            {t("teacher.creditsHeading")}
          </span>
          <span style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span
              data-testid="student-billing-balance"
              style={{ font: "var(--fw-bold) var(--fs-2xl)/1 var(--font-mono)", color: "var(--text-strong)" }}
            >
              {balance}
            </span>
            <span style={{ font: "var(--fw-medium) var(--fs-2xs)/1 var(--font-body)", color: "var(--text-muted)" }}>
              {t("teacher.creditsCount", { count: balance })}
            </span>
          </span>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          {PRESETS.map((preset) => (
            <Button
              key={preset}
              variant="secondary"
              size="sm"
              disabled={busy || !online}
              onClick={() => setAmount(String(preset))}
              data-testid={`student-billing-preset-${preset}`}
            >
              {`+${preset}`}
            </Button>
          ))}
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 8, marginTop: 10 }}>
          <div style={{ flex: "0 0 96px" }}>
            <Input
              type="number"
              min={1}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              aria-label={t("teacher.creditsAmountLabel")}
              data-testid="student-billing-amount"
            />
          </div>
          <div style={{ flex: "1 1 160px", minWidth: 0 }}>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("teacher.creditsNotePlaceholder")}
              aria-label={t("teacher.creditsNoteLabel")}
              data-testid="student-billing-note"
            />
          </div>
          <Button
            variant="primary"
            size="sm"
            disabled={busy || !online}
            onClick={recharge}
            data-testid="student-billing-recharge"
            style={{ marginLeft: "auto" }}
          >
            {t("teacher.creditsRecharge")}
          </Button>
        </div>

        {/* validity window for the next recharge (issue #327) */}
        <div style={{ marginTop: 12 }} data-testid="student-billing-validity">
          <div style={{ ...LABEL_STYLE, marginBottom: 6 }}>
            {t("teacher.creditsValidityLabel")}
          </div>
          <SegmentedControl
            size="sm"
            value={validity}
            onChange={setValidity}
            options={[
              { value: "none", label: t("teacher.creditsValidityNone") },
              ...VALIDITY_MONTHS.map((m) => ({
                value: String(m),
                label: t("teacher.creditsValidityMonthsShort", { count: m }),
              })),
            ]}
          />
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 8, marginTop: 8 }}>
            <div style={{ flex: "0 0 170px" }}>
              <Input
                type="date"
                label={t("teacher.creditsValidityDateLabel")}
                min={today}
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
                data-testid="student-billing-valid-until"
              />
            </div>
            <div
              data-testid="student-billing-validity-hint"
              style={{
                flex: "1 1 120px",
                // The hint is a sentence; `anywhere` keeps a single long word
                // from forcing the row wider than the sheet.
                overflowWrap: "anywhere",
                paddingBottom: 8,
                font: "var(--fw-regular) var(--fs-2xs)/1.4 var(--font-body)",
                color: "var(--text-faint)",
              }}
            >
              {validUntil !== ""
                ? t("teacher.creditsValidityDateHint", { date: formatShortDate(validUntil) })
                : validity === "none"
                  ? t("teacher.creditsValidityNeverHint")
                  : t("teacher.creditsValidityMonthsHint", { count: Number(validity) })}
            </div>
          </div>
        </div>

        {rechargeMutation.isError && (
          <div role="alert" data-testid="student-billing-credits-error" style={{ ...ERROR_STYLE, marginTop: 10 }}>
            {rechargeMutation.error?.body?.error === "invalid_expiry"
              ? t("teacher.creditsValidityError")
              : (rechargeMutation.error?.message ?? t("teacher.creditsError"))}
          </div>
        )}
      </div>

      {/* ledger */}
      <div style={{ ...SECTION_STYLE, marginTop: "var(--space-4)" }}>
        <div style={{ ...LABEL_STYLE, marginBottom: 10 }}>{t("teacher.ledgerHeading")}</div>

        {creditsQuery.isLoading && (
          <div data-testid="student-billing-ledger-loading" style={{ color: "var(--text-faint)", font: "var(--text-body-base)" }}>
            {t("common.loading")}
          </div>
        )}

        {!creditsQuery.isLoading && creditsQuery.isError && (
          online ? (
            <div role="alert" data-testid="student-billing-ledger-error" style={ERROR_STYLE}>
              {creditsQuery.error?.message ?? t("teacher.ledgerError")}
            </div>
          ) : (
            <OfflineErrorState />
          )
        )}

        {!creditsQuery.isLoading && !creditsQuery.isError && entries.length === 0 && (
          <div data-testid="student-billing-ledger-empty" style={{ color: "var(--text-faint)", font: "var(--text-body-base)" }}>
            {t("teacher.ledgerEmpty")}
          </div>
        )}

        {!creditsQuery.isLoading && !creditsQuery.isError && entries.length > 0 && (
          <div data-testid="student-billing-ledger" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {entries.map((entry) => (
              <LedgerRow
                key={entry.id}
                entry={entry}
                entries={entries}
                busy={busy || !online}
                onReverse={startReverse}
              />
            ))}
          </div>
        )}
      </div>

      {/* focused reversal modal (issue #311) — same bottom-sheet card as the
          sheet it opens from, capped to the viewport and scrollable so its
          fields and buttons stay reachable on a short screen (or with the
          keyboard open) */}
      {reversing && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t("teacher.reverseHeading")}
          data-testid="student-billing-reverse-dialog"
          style={SHEET_OVERLAY_STYLE}
          onClick={closeReverse}
        >
          <Card
            padding="var(--space-5)"
            onClick={(e) => e.stopPropagation()}
            style={SHEET_STYLE}
          >
            <div style={{ ...LABEL_STYLE, marginBottom: 8 }}>
              {t("teacher.reverseHeading")}
            </div>
            <div
              style={{
                font: "var(--fw-semibold) var(--fs-sm)/1.3 var(--font-body)",
                color: "var(--text-strong)",
              }}
            >
              {t(creditKindLabelKey(reversing.kind))}
              <span style={{ color: "var(--text-muted)", fontWeight: "var(--fw-regular)" }}>
                {" · "}
                {formatCreditAmount(reversing.amount)}
              </span>
            </div>
            <div
              style={{
                font: "var(--fw-regular) var(--fs-sm)/1.4 var(--font-body)",
                color: "var(--text-muted)",
                marginTop: 6,
              }}
            >
              {t("teacher.reverseIntro", { count: reverseMax })}
            </div>

            <div style={{ marginTop: 14 }}>
              <Input
                type="number"
                min={1}
                max={reverseMax}
                value={reverseAmount}
                onChange={(e) => setReverseAmount(e.target.value)}
                label={t("teacher.reverseAmountLabel")}
                data-testid="student-billing-reverse-amount"
              />
            </div>

            <div style={{ marginTop: 10 }}>
              <Input
                value={reverseNote}
                onChange={(e) => setReverseNote(e.target.value)}
                placeholder={t("teacher.reverseNotePlaceholder")}
                label={t("teacher.reverseNoteLabel")}
                data-testid="student-billing-reverse-note"
              />
            </div>

            {reverseMutation.isError && (
              <div
                role="alert"
                data-testid="student-billing-reverse-error"
                style={{ ...ERROR_STYLE, marginTop: 10 }}
              >
                {reverseErrorText}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <Button
                variant="ghost"
                size="sm"
                onClick={closeReverse}
                data-testid="student-billing-reverse-cancel"
              >
                {t("teacher.reverseCancel")}
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={
                  reverseMutation.isPending || !online || reverseAmountInvalid || reverseNoteMissing
                }
                onClick={confirmReverse}
                data-testid="student-billing-reverse-submit"
              >
                {t("teacher.reverseSubmit")}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

function LedgerRow({
  entry,
  entries,
  busy,
  onReverse,
}: {
  entry: CreditEntry;
  entries: CreditEntry[];
  busy: boolean;
  onReverse: (entry: CreditEntry) => void;
}) {
  const { t } = useTranslation();
  const positive = entry.amount > 0;
  const remaining = remainingReversible(entry, entries);
  const canReverse = isReversible(entry) && remaining > 0;

  return (
    <div
      id={`credit-entry-${entry.id}`}
      data-testid={`student-billing-ledger-entry-${entry.id}`}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: "var(--space-2) var(--space-3)",
        background: "var(--surface-muted)",
        borderRadius: "var(--radius-md)",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            font: "var(--fw-semibold) var(--fs-sm)/1.2 var(--font-body)",
            color: "var(--text-strong)",
            // Teacher notes are free text: an email or a URL must not stretch
            // the row past the sheet.
            overflowWrap: "anywhere",
          }}
        >
          {t(creditKindLabelKey(entry.kind))}
          {entry.note ? <span style={{ color: "var(--text-muted)", fontWeight: "var(--fw-regular)" }}> · {entry.note}</span> : null}
        </div>
        <div
          style={{
            font: "var(--fw-regular) var(--fs-2xs)/1.4 var(--font-body)",
            color: "var(--text-faint)",
            marginTop: 2,
            overflowWrap: "anywhere",
          }}
        >
          <Meta entry={entry} />
          {entry.kind === "reversal" && entry.reverses_id !== null ? (
            <>
              {" · "}
              <a
                href={`#credit-entry-${entry.reverses_id}`}
                data-testid={`student-billing-ledger-reverses-${entry.id}`}
                style={{ color: "var(--brand)" }}
              >
                {t("teacher.ledgerReversesEntry", { id: entry.reverses_id })}
              </a>
            </>
          ) : null}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flex: "none" }}>
        <span
          style={{
            font: "var(--fw-bold) var(--fs-lg)/1 var(--font-mono)",
            color: positive ? "var(--status-success-fg)" : "var(--text-strong)",
          }}
        >
          {formatCreditAmount(entry.amount)}
        </span>
        {canReverse && (
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => onReverse(entry)}
            data-testid={`student-billing-reverse-${entry.id}`}
          >
            {t("teacher.ledgerReverse")}
          </Button>
        )}
      </div>
    </div>
  );
}

function Meta({ entry }: { entry: CreditEntry }): ReactNode {
  const { t } = useTranslation();
  const date = formatLongDate(entry.inserted_at);
  const base = entry.inserted_by
    ? t("teacher.ledgerBy", { name: displayName(entry.inserted_by), date })
    : date;

  const extra: string[] = [];

  if (entry.expires_at) {
    extra.push(t("teacher.ledgerExpiresOn", { date: formatShortDate(entry.expires_at) }));
  }

  if (entry.remaining !== null && (entry.kind === "recharge" || entry.kind === "comp")) {
    extra.push(t("teacher.ledgerGrantRemaining", { count: entry.remaining }));
  }

  return extra.length === 0 ? base : `${base} · ${extra.join(" · ")}`;
}
